import {envIsPublicCloud} from '../../../config';
import {useOrganizationFlagsQuery} from '../../../generated/graphql';
import {jsonParseSafe} from '../../../util/json';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../OrgRoles/RolesDashboard';
import {parseWeaveSensitiveDataPolicy} from './sensitiveDataPolicy';

export const API_KEY_CREATION_DISABLED_MESSAGE =
  'API key creation has been disabled by your organization administrator. You can still edit, delete, and use existing API keys.';

/**
 * Whether the org-level "Disable API key creation" policy is on. The setting
 * is dedicated/on-prem only, so this always returns false on public cloud.
 */
export function useIsApiKeyCreationDisabled(): boolean {
  const {data} = useOrganizationPrivacySettings(
    DEFAULT_ONPREM_ORGANIZATION_NAME,
    {skip: envIsPublicCloud}
  );
  return data.disableApiKeyCreation;
}

export function useOrganizationPrivacySettings(
  orgName: string,
  options?: {skip?: boolean}
) {
  const {loading, refetch, data, error} = useOrganizationFlagsQuery({
    variables: {
      orgName,
    },
    skip: options?.skip,
    fetchPolicy: 'cache-and-network',
  });

  const parsedOrgFlags =
    data?.organization?.flags != null
      ? jsonParseSafe(data?.organization?.flags)
      : {};

  return {
    loading,
    error,
    refetch,
    data: {
      orgId: data?.organization?.id ?? '',
      hidden: parsedOrgFlags.is_hidden ?? false,
      privateOnly: parsedOrgFlags.private_projects ?? false,
      onlyAdminsCanInvite: parsedOrgFlags.only_admins_can_invite ?? false,
      disableMagicLinkSharing:
        parsedOrgFlags.disable_magic_link_sharing ?? false,
      disableApiKeyCreation: parsedOrgFlags.disable_api_key_creation ?? false,
      restrictMatchingOrgEmailDomainUsers:
        parsedOrgFlags.restrict_matching_org_email_domain_users ?? false,
      codeSavingDisabled: parsedOrgFlags.code_saving_disabled ?? false,
      enableLLMPoweredFeatures:
        parsedOrgFlags.enable_llm_powered_features ?? false,
      shareAITracesWithWB: parsedOrgFlags.share_ai_traces_with_wb ?? false,
      allowModelTrainingOnSharedData:
        parsedOrgFlags.allow_model_training_on_shared_data ?? false,
      ariaProjectMemoryEnabled:
        parsedOrgFlags.aria_project_memory_enabled ?? false,
      restrictInvitesToClaimedDomain:
        parsedOrgFlags.restrict_invites_to_claimed_domain ?? false,
      requireOrgScopedAPIKeys:
        parsedOrgFlags.require_org_scoped_api_keys ?? false,
      emailDomain: parsedOrgFlags.email_domain ?? null,
      weaveSensitiveDataPolicy: parseWeaveSensitiveDataPolicy(
        parsedOrgFlags.weave_sensitive_data_policy
      ),
    },
  };
}
